import { WorkspaceType } from '../../graphql/enums';
import { AssetFilterBaseColumns, AssetFilterModel } from './assetFilterModel';

type WorkspaceColumnConfig = {
  /** Columns to replace: source column name → replacement column name */
  columnReplacements: Record<string, string>;
  /** Columns exclusive to this workspace type (filtered out for other types) */
  exclusiveColumns: string[];
  /** Extra columns to insert into defaults when resetting */
  defaultInsertions?: Array<{ column: string; after: string }>;
  /** Export reference field config for the "description-like" column */
  exportReference: { key: string; labelKey: string };
};

const workspaceColumnConfigs: Partial<
  Record<WorkspaceType, WorkspaceColumnConfig>
> = {
  [WorkspaceType.Oem]: {
    columnReplacements: { description: 'serialNumber' },
    exclusiveColumns: ['serialNumber', 'productName', 'addons'],
    defaultInsertions: [{ column: 'productName', after: 'serialNumber' }],
    exportReference: { key: 'serialNumber', labelKey: 'asset.serialNumber' },
  },
};

const defaultExportReference = {
  key: 'description',
  labelKey: 'asset.description',
};

function getConfig(
  workspaceType?: WorkspaceType
): WorkspaceColumnConfig | undefined {
  return workspaceType ? workspaceColumnConfigs[workspaceType] : undefined;
}

/**
 * Collect all exclusive columns that do NOT belong to the given workspace type.
 */
function getExcludedColumns(workspaceType?: WorkspaceType): Set<string> {
  const excluded = new Set<string>();
  for (const [type, config] of Object.entries(workspaceColumnConfigs)) {
    if (type !== workspaceType) {
      for (const col of config.exclusiveColumns) {
        excluded.add(col);
      }
    }
  }
  return excluded;
}

/**
 * Transform a column array for a given workspace type:
 * 1. Apply column replacements (e.g. description → serialNumber for OEM)
 * 2. Remove columns exclusive to other workspace types
 */
export function resolveColumnsForWorkspace(
  columns: string[],
  workspaceType?: WorkspaceType
): string[] {
  const config = getConfig(workspaceType);
  const excluded = getExcludedColumns(workspaceType);

  let resolved = config
    ? columns.map((col) => config.columnReplacements[col] ?? col)
    : [...columns];

  resolved = resolved.filter((col) => !excluded.has(col));

  return resolved;
}

/**
 * Get the available base columns for the column picker, resolved for the workspace type.
 */
export function getBaseColumnsForWorkspace(
  workspaceType?: WorkspaceType
): string[] {
  return resolveColumnsForWorkspace(AssetFilterBaseColumns, workspaceType);
}

/**
 * Get the default column selection for a workspace type (used on reset).
 * Applies replacements, filters exclusives, and inserts type-specific defaults.
 */
export function getDefaultColumnsForWorkspace(
  workspaceType?: WorkspaceType
): string[] {
  const raw = new AssetFilterModel().columns ?? [];
  let defaults = resolveColumnsForWorkspace(raw, workspaceType);

  const config = getConfig(workspaceType);
  if (config?.defaultInsertions) {
    for (const insertion of config.defaultInsertions) {
      const idx = defaults.indexOf(insertion.after);
      if (idx !== -1 && !defaults.includes(insertion.column)) {
        defaults = [
          ...defaults.slice(0, idx + 1),
          insertion.column,
          ...defaults.slice(idx + 1),
        ];
      }
    }
  }

  return defaults;
}

/**
 * Get the export reference config (key + i18n label key) for the workspace type.
 */
export function getExportReference(workspaceType?: WorkspaceType): {
  key: string;
  labelKey: string;
} {
  const config = getConfig(workspaceType);
  return config?.exportReference ?? defaultExportReference;
}
